import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  ChevronDownIcon,
  PaperAirplaneIcon,
  XCircleIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";

import { createNote, fetchNotes } from "../../../api/notes";
import { axiosWithToast, dateToString } from "../../Utils";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { DocumentUploadSectionV4 } from "../Media/DocumentUploadSectionV4";
import { TinyDocumentSection } from "../documents/tinyDocumentSection";
import { TemplateDialogButton } from "../templates/template-dialog-button";
import { PMDialog } from "../../utils/PMDialog";
import { TWclassNames } from "../../utils/Div";
import {
  ContactTitle,
  CustomerTitle,
  ProjectTitle,
  ReportTitle,
} from "../../Utils";
import { ContactsSelector } from "../../utils/moduleSelectors/contactSelector";
import { CustomerSelector } from "../../utils/moduleSelectors/customerSelector";
import { ReportSelector } from "../../utils/moduleSelectors/reportSelector";
import { ProjectSelector } from "../../utils/moduleSelectors/projectSelector";
import {
  CaseItemDescription,
  MentionTextEditor,
} from "../../utils/MentionTextEditor";

const MOBILE_BREAKPOINT = 1024;
const INITIAL_VISIBLE_NOTES = 3;

const TAG_MODULES = [
  { key: "report", label: "Report" },
  { key: "contact", label: "Contact" },
  { key: "customer", label: "Customer" },
  { key: "project", label: "Project" },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < MOBILE_BREAKPOINT;
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia(
      `(max-width: ${MOBILE_BREAKPOINT - 1}px)`
    );

    const handleChange = (event) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  return isMobile;
}

function stripRichText(value) {
  if (typeof value !== "string") return "";

  return value
    .replace(/<p><br><\/p>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function hasComposerContent(values) {
  return Boolean(
    stripRichText(values?.note) ||
      values?.documents?.length ||
      values?.tags?.length
  );
}

function getInitialValues(module, module_param) {
  return {
    id: null,
    note: "",
    module,
    module_param,
    documents: [],
    tags: [],
    submit: null,
  };
}

function tagColor(tag) {
  switch (tag?.module) {
    case "report":
      return "bg-blue-50 text-blue-700 ring-blue-200";
    case "contact":
      return "bg-emerald-50 text-emerald-700 ring-emerald-200";
    case "customer":
      return "bg-rose-50 text-rose-700 ring-rose-200";
    case "project":
      return "bg-amber-50 text-amber-700 ring-amber-200";
    default:
      return "bg-gray-100 text-gray-700 ring-gray-200";
  }
}

function TagTitle({ tag }) {
  const object = tag?.object?.object || tag?.object;

  switch (tag?.module) {
    case "report":
      return object ? <ReportTitle report={object} /> : "Unknown Report";
    case "contact":
      return object ? <ContactTitle contact={object} /> : "Unknown Contact";
    case "customer":
      return object ? (
        <CustomerTitle customer={object} useDialog={true} />
      ) : (
        "Unknown Customer"
      );
    case "project":
      return object ? <ProjectTitle project={object} /> : "Unknown Project";
    default:
      return object?.displayTitle || `${tag?.module || "Unknown"}`;
  }
}

function TagsView({ tags, onDeleteTag }) {
  if (!Array.isArray(tags) || tags.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {tags.map((tag, index) => {
        if (!tag) return null;

        return (
          <span
            key={`${tag?.module || "unknown"}-${tag?.module_param || index}`}
            className={TWclassNames(
              "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
              tagColor(tag)
            )}
          >
            <span className="truncate">
              <TagTitle tag={tag} />
            </span>
            {onDeleteTag && !tag?.id && (
              <button
                type="button"
                className="rounded-full p-0.5 transition-colors hover:bg-black/5"
                onClick={() => onDeleteTag(index)}
              >
                <XCircleIcon className="h-4 w-4" />
              </button>
            )}
          </span>
        );
      })}
    </div>
  );
}

function TagSelector({ moduleAction }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const toggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 6,
        left: rect.left + window.scrollX,
      });
    }
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutside = (event) => {
      if (buttonRef.current?.contains(event.target)) return;
      if (dropdownRef.current?.contains(event.target)) return;
      setIsOpen(false);
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleOutside);
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleOutside);
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        className={TWclassNames(
          "inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium text-gray-600 transition-colors",
          isOpen ? "bg-gray-100 text-blue-700" : "hover:bg-gray-100"
        )}
      >
        <span>{t("Tags")}</span>
        <ChevronDownIcon
          className={TWclassNames(
            "h-4 w-4 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          style={{
            position: "absolute",
            top: position.top,
            left: position.left,
            zIndex: 99999,
          }}
          className="w-56 rounded-xl border border-gray-200 bg-white p-2 shadow-2xl"
        >
          {TAG_MODULES.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => {
                moduleAction(item.key);
                setIsOpen(false);
              }}
              className="block w-full rounded-lg px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-blue-50 hover:text-blue-700"
            >
              {t(item.label)}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function NoteCard({ note, refreshTrigger, embedded }) {
  return (
    <article
      className={TWclassNames(
        "rounded-lg border bg-white p-4",
        embedded
          ? "border-gray-100 shadow-none"
          : "border-gray-200 shadow-sm"
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900">
            {note?.created_by?.displayTitle || "Unknown User"}
          </p>
          <p className="text-xs text-gray-500">{dateToString(note?.created_at)}</p>
        </div>
      </div>

      <div className="mt-3 text-sm text-gray-700">
        <CaseItemDescription value={note?.note || ""} />
      </div>

      {Array.isArray(note?.tags) && note.tags.length > 0 && (
        <div className="mt-3">
          <TagsView tags={note.tags} />
        </div>
      )}

      <div className="mt-3">
        <TinyDocumentSection
          showNoDocuments={false}
          pathType="url"
          module="NOTE"
          moduleParam={note?.id}
          documents={Array.isArray(note?.documents) ? note.documents : []}
          refreshTrigger={refreshTrigger}
          refreshButton={false}
          maxDocumentDisplay={3}
        />
      </div>
    </article>
  );
}

function MobileComposerButton({ onClick, hasDraft, placeholder }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-xl border border-dashed border-blue-200 bg-blue-50/70 p-4 text-left transition-colors hover:border-blue-300 hover:bg-blue-50"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-white p-2 text-blue-600 shadow-sm ring-1 ring-blue-100">
          <DocumentTextIcon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">
            {hasDraft ? "Fortsett arbeidsrapport" : "Skriv arbeidsrapport"}
          </p>
          <p className="mt-1 text-sm text-gray-600">
            {hasDraft
              ? "Åpne full visning for å redigere tekst og vedlegg."
              : placeholder}
          </p>
        </div>
      </div>
    </button>
  );
}

function ComposerSurface({
  disabled,
  enableFileUpload,
  enableMentions,
  enableTags,
  enableTemplates,
  formik,
  module_param,
  moduleAction,
  onDeleteTag,
  placeholder,
  submitLabel,
  embedded,
}) {
  const { t } = useTranslation();
  const showAttachments = Array.isArray(formik.values.documents)
    ? formik.values.documents.length > 0
    : false;

  return (
    <div
      className={TWclassNames(
        "overflow-hidden rounded-xl border bg-white",
        embedded
          ? "border-gray-100 shadow-none"
          : "border-gray-200 shadow-sm",
        disabled && "opacity-70"
      )}
    >
      <div className="border-b border-gray-100 px-4 py-3">
        <MentionTextEditor
          formik={formik}
          name="note"
          placeholder={placeholder}
          minHeight={120}
          maxHeight={220}
          disabled={disabled}
          enableMentions={enableMentions}
          enableSlashCommands={enableTags}
          onTagSelect={(tag) =>
            formik.setFieldValue("tags", [
              ...(formik.values.tags || []),
              tag,
            ])
          }
        />
      </div>

      {(formik.values.tags?.length > 0 || showAttachments) && (
        <div className="space-y-3 border-b border-gray-100 bg-gray-50/70 px-4 py-3">
          {formik.values.tags?.length > 0 && (
            <TagsView tags={formik.values.tags} onDeleteTag={onDeleteTag} />
          )}

          {showAttachments && (
            <TinyDocumentSection
              title="Vedlegg"
              showNoDocuments={false}
              refreshButton={false}
              module="NOTE"
              moduleParam={module_param}
              documents={formik.values.documents}
              maxDocumentDisplay={4}
            />
          )}
        </div>
      )}

      <div
        className={TWclassNames(
          "flex items-center gap-2 overflow-x-auto px-4 py-3",
          embedded ? "bg-gray-50/80" : "bg-gray-50"
        )}
      >
        <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-2">
          {enableFileUpload && !disabled && (
            <DocumentUploadSectionV4
              multiple={true}
              iconOnly={true}
              compact={true}
              module="NOTE"
              module_param={module_param}
              parentCallback={(uploadedDocuments) => {
                const mergedDocuments = [
                  ...(formik.values.documents || []),
                  ...uploadedDocuments,
                ].filter(
                  (document, index, array) =>
                    array.findIndex((item) => item.id === document.id) === index
                );

                formik.setFieldValue("documents", mergedDocuments);
              }}
            />
          )}

          {enableTags && !disabled && <TagSelector moduleAction={moduleAction} />}

          {enableTemplates && !disabled && (
            <TemplateDialogButton fieldName="note" type="button" styleType="custom" className="!w-auto shrink-0 whitespace-nowrap rounded-lg px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-700" {...formik} />
          )}
        </div>

        <button
          type="button"
          onClick={formik.submitForm}
          disabled={disabled || formik.isSubmitting || !hasComposerContent(formik.values)}
          className={TWclassNames(
            "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-colors",
            disabled || formik.isSubmitting || !hasComposerContent(formik.values)
              ? "cursor-not-allowed bg-gray-200 text-gray-400"
              : "bg-blue-600 text-white hover:bg-blue-700"
          )}
        >
          <PaperAirplaneIcon className="h-4 w-4" />
          {formik.isSubmitting ? t("Lagrer...") : submitLabel}
        </button>
      </div>

      {(formik.errors.note || formik.errors.submit) && (
        <div className="border-t border-red-100 bg-red-50 px-4 py-2 text-xs text-red-700">
          {formik.errors.note || formik.errors.submit}
        </div>
      )}
    </div>
  );
}

const NotesAdvancedSectionComponent = ({
  module,
  module_param,
  enableFileUpload = true,
  enableTemplates = true,
  enableTags = true,
  enableMentions = true,
  title = "Notater",
  subtitle = null,
  disabled = false,
  placeholder = "Skriv en oppdatering eller rapport, og legg ved bilder ved behov.",
  submitLabel = "Legg til notat",
  mobileFullscreenComposer = false,
  /** When true, skip outer card chrome (parent provides one surface). */
  embedded = false,
  /**
   * Read-only legacy notes: no create UI. Renders nothing when empty.
   * Collapsed by default under «Tidligere notater».
   */
  legacyOnly = false,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showAllNotes, setShowAllNotes] = useState(false);
  const [openModuleDialog, setOpenModuleDialog] = useState(null);
  const [documentRefreshTrigger, setDocumentRefreshTrigger] = useState(0);
  const [composerDialogOpen, setComposerDialogOpen] = useState(false);
  const [legacyExpanded, setLegacyExpanded] = useState(false);

  const getNotes = useCallback(
    async (page = 1, append = false) => {
      if (page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const controller = {
          filters: [{ name: module, value: module_param }],
          page,
          per_page: 10,
          query: "",
          sort: "desc",
          sortBy: "updated_at",
          view: "all",
        };

        const response = await fetchNotes(controller);
        const fetchedNotes = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

        setNotes((prevNotes) =>
          append ? [...prevNotes, ...fetchedNotes] : fetchedNotes
        );

        const metaLastPage =
          response?.data?.meta?.last_page || response?.data?.last_page;
        setHasMore(
          metaLastPage ? page < metaLastPage : fetchedNotes.length === 10
        );
        setDocumentRefreshTrigger((prev) => prev + 1);
      } catch (error) {
        console.error("Error fetching notes:", error);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [module, module_param]
  );

  useEffect(() => {
    if (!module || !module_param) return;

    setCurrentPage(1);
    setShowAllNotes(false);
    getNotes(1, false);
  }, [getNotes, module, module_param]);

  const loadMoreNotes = useCallback(() => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    getNotes(nextPage, true);
  }, [currentPage, getNotes]);

  const moduleAction = useCallback((type) => {
    setOpenModuleDialog(null);
    setTimeout(() => setOpenModuleDialog(type), 25);
  }, []);

  const handleModuleSelectorOnClose = useCallback((state) => {
    if (!state) {
      setOpenModuleDialog(null);
    }
  }, []);

  if (!module || !module_param) {
    if (legacyOnly) return null;
    console.error("NotesAdvancedSection: Missing required props", {
      module,
      module_param,
      title,
      subtitle,
    });

    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-4 text-center text-sm text-red-600">
        Error: Missing required module or module_param
      </div>
    );
  }

  const displayedNotes = showAllNotes
    ? notes
    : notes.slice(0, INITIAL_VISIBLE_NOTES);
  const hasHiddenLoadedNotes =
    notes.length > INITIAL_VISIBLE_NOTES && !showAllNotes;
  const shouldShowLoadMore = hasHiddenLoadedNotes || hasMore;

  const shellClass = embedded
    ? "space-y-5"
    : "rounded-2xl border border-gray-200 bg-white shadow-sm";

  const notesList = (
    <div className="space-y-3">
      {!legacyOnly && (
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium text-gray-800">
            {t("Siste oppdateringer")}
          </h3>
          {isLoadingMore && (
            <span className="text-xs text-gray-500">{t("Laster...")}</span>
          )}
        </div>
      )}

      {isLoading ? (
        <ResourceLoading />
      ) : displayedNotes.length > 0 ? (
        <div className="space-y-3">
          {displayedNotes.map((note) => (
            <NoteCard
              key={note?.id || `${note?.created_at}-${note?.created_by?.id}`}
              note={note}
              refreshTrigger={documentRefreshTrigger}
              embedded={embedded || legacyOnly}
            />
          ))}

          {shouldShowLoadMore && (
            <div className="flex justify-center pt-1">
              <button
                type="button"
                onClick={() => {
                  if (hasHiddenLoadedNotes) {
                    setShowAllNotes(true);
                    return;
                  }

                  loadMoreNotes();
                }}
                disabled={isLoadingMore}
                className="min-h-[40px] rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoadingMore
                  ? t("Laster...")
                  : hasHiddenLoadedNotes
                    ? t("Vis flere")
                    : t("Last flere")}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div
          className={TWclassNames(
            "rounded-lg border border-dashed px-4 py-6 text-center text-sm text-gray-500",
            embedded
              ? "border-gray-100 bg-gray-50/50"
              : "border-gray-200 bg-gray-50"
          )}
        >
          {t("Ingen notater registrert ennå.")}
        </div>
      )}
    </div>
  );

  // Legacy read-only: hide when empty; collapsed disclosure when notes exist.
  if (legacyOnly) {
    if (isLoading || notes.length === 0) return null;

    const legacyTitle = title || t("notes.legacy_title");

    return (
      <div
        className={
          embedded
            ? "rounded-xl border border-amber-200/80 bg-amber-50/40"
            : "rounded-2xl border border-amber-200/80 bg-amber-50/40 shadow-sm"
        }
      >
        <button
          type="button"
          onClick={() => setLegacyExpanded((v) => !v)}
          className="flex w-full min-h-[44px] items-center justify-between gap-3 px-4 py-3 text-left sm:px-5"
          aria-expanded={legacyExpanded}
        >
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">{legacyTitle}</p>
            <p className="mt-0.5 text-xs text-gray-500">
              {t("notes.legacy_hint")}
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-2">
            <span className="rounded-full bg-white/80 px-2.5 py-1 text-xs font-medium text-gray-600 ring-1 ring-amber-200/60">
              {notes.length}
            </span>
            <ChevronDownIcon
              className={TWclassNames(
                "h-5 w-5 text-gray-500 transition-transform",
                legacyExpanded && "rotate-180"
              )}
              aria-hidden
            />
          </span>
        </button>
        {legacyExpanded ? (
          <div className="space-y-3 border-t border-amber-200/60 px-4 py-4 sm:px-5">
            {notesList}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={shellClass}>
      <div
        className={TWclassNames(
          "flex items-start justify-between gap-4 border-gray-100",
          embedded
            ? "border-b pb-4"
            : "border-b px-4 py-4 sm:px-5"
        )}
      >
        <div className="min-w-0">
          <h2
            className={TWclassNames(
              "font-semibold text-gray-900",
              embedded ? "text-lg" : "text-base"
            )}
          >
            {title}
          </h2>
          {subtitle && (
            <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
          )}
        </div>
        <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
          {notes.length} {t("notater")}
        </span>
      </div>

      <div className={embedded ? "space-y-5" : "space-y-4 px-4 py-4 sm:px-5"}>
        <Formik
          enableReinitialize={true}
          initialValues={getInitialValues(module, module_param)}
          validationSchema={Yup.object({
            note: Yup.string()
              .nullable()
              .test(
                "has-content",
                "Skriv en beskrivelse eller legg ved bilde",
                function validateContent(value) {
                  return Boolean(
                    stripRichText(value) ||
                      this.parent?.documents?.length ||
                      this.parent?.tags?.length
                  );
                }
              ),
          })}
          onSubmit={async (values, helpers) => {
            try {
              await axiosWithToast(
                createNote({
                  ...values,
                  note: values.note || " ",
                }),
                null,
                null,
                (savedNote) => {
                  setNotes((prevNotes) => [savedNote, ...prevNotes]);
                  setDocumentRefreshTrigger((prev) => prev + 1);
                  if (mobileFullscreenComposer) {
                    setComposerDialogOpen(false);
                  }
                },
                "Lagrer notat",
                (errors) => {
                  helpers.setErrors({
                    submit: Array.isArray(errors)
                      ? errors.join(", ")
                      : "Kunne ikke lagre notatet.",
                  });
                }
              );

              helpers.resetForm({
                values: getInitialValues(module, module_param),
              });
            } catch (error) {
              console.error("Error creating note:", error);
              toast.error("Something went wrong...");
              helpers.setErrors({
                submit: error?.message || "Kunne ikke lagre notatet.",
              });
            } finally {
              helpers.setSubmitting(false);
            }
          }}
        >
          {(formik) => {
            const handleDeleteTag = (index) => {
              const nextTags = [...(formik.values.tags || [])];
              nextTags.splice(index, 1);
              formik.setFieldValue("tags", nextTags);
            };

            const handleModuleChange = (type, event) => {
              const payload = {
                module: type,
                module_param: event?.target?.value,
                object: event?.target?.data?.object || event?.target?.data,
              };

              formik.setFieldValue("tags", [
                ...(formik.values.tags || []),
                payload,
              ]);
              setOpenModuleDialog(null);
            };

            const composer = (
              <ComposerSurface
                disabled={disabled}
                enableFileUpload={enableFileUpload}
                enableMentions={enableMentions}
                enableTags={enableTags}
                enableTemplates={enableTemplates}
                formik={formik}
                module_param={module_param}
                moduleAction={moduleAction}
                onDeleteTag={handleDeleteTag}
                placeholder={placeholder}
                submitLabel={submitLabel}
                embedded={embedded}
              />
            );

            return (
              <Form className="space-y-4">
                {mobileFullscreenComposer && isMobile ? (
                  <>
                    <MobileComposerButton
                      onClick={() => setComposerDialogOpen(true)}
                      hasDraft={hasComposerContent(formik.values)}
                      placeholder={placeholder}
                    />

                    <PMDialog
                      open={composerDialogOpen}
                      onClose={() => setComposerDialogOpen(false)}
                      title={title}
                      size="4xl"
                      dense={true}
                    >
                      <div className="flex h-full flex-col gap-4 overflow-y-auto px-4 pb-4">
                        {subtitle && (
                          <p className="text-sm text-gray-500">{subtitle}</p>
                        )}
                        {composer}
                      </div>
                    </PMDialog>
                  </>
                ) : (
                  composer
                )}

                <ContactsSelector
                  showField={false}
                  open={openModuleDialog === "contact"}
                  onClose={handleModuleSelectorOnClose}
                  onChange={(data) => handleModuleChange("contact", data)}
                />

                <CustomerSelector
                  showField={false}
                  open={openModuleDialog === "customer"}
                  onClose={handleModuleSelectorOnClose}
                  onChange={(data) => handleModuleChange("customer", data)}
                />

                <ReportSelector
                  showField={false}
                  open={openModuleDialog === "report"}
                  onClose={handleModuleSelectorOnClose}
                  onChange={(data) => handleModuleChange("report", data)}
                />

                <ProjectSelector
                  showField={false}
                  open={openModuleDialog === "project"}
                  onClose={handleModuleSelectorOnClose}
                  onChange={(data) => handleModuleChange("project", data)}
                />
              </Form>
            );
          }}
        </Formik>

        {notesList}
      </div>
    </div>
  );
};

export const NotesAdvancedSection = memo(
  NotesAdvancedSectionComponent,
  (prevProps, nextProps) =>
    prevProps.module === nextProps.module &&
    prevProps.module_param === nextProps.module_param &&
    prevProps.enableFileUpload === nextProps.enableFileUpload &&
    prevProps.enableTemplates === nextProps.enableTemplates &&
    prevProps.enableTags === nextProps.enableTags &&
    prevProps.enableMentions === nextProps.enableMentions &&
    prevProps.title === nextProps.title &&
    prevProps.subtitle === nextProps.subtitle &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.placeholder === nextProps.placeholder &&
    prevProps.submitLabel === nextProps.submitLabel &&
    prevProps.mobileFullscreenComposer === nextProps.mobileFullscreenComposer &&
    prevProps.embedded === nextProps.embedded &&
    prevProps.legacyOnly === nextProps.legacyOnly
);

NotesAdvancedSection.displayName = "NotesAdvancedSection";



